import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { User } from "oidc-client-ts";
import { getUser, getUserManager, login, logout } from "./userManager";

export interface AuthState {
  user: User | null;
  /** True until the initial session check completes. */
  loading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    getUser()
      .then((found) => {
        if (!cancelled) setUser(found);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Keep React in step with the silent-renew and expiry events the manager
    // raises, so a renewed token reaches the query layer without a reload.
    const manager = getUserManager();
    const onLoaded = (loaded: User) => setUser(loaded);
    const onUnloaded = () => setUser(null);
    const onExpired = () => setUser(null);

    manager.events.addUserLoaded(onLoaded);
    manager.events.addUserUnloaded(onUnloaded);
    manager.events.addAccessTokenExpired(onExpired);

    return () => {
      cancelled = true;
      manager.events.removeUserLoaded(onLoaded);
      manager.events.removeUserUnloaded(onUnloaded);
      manager.events.removeAccessTokenExpired(onExpired);
    };
  }, []);

  const doLogin = useCallback(async () => {
    setError(null);
    await login();
  }, []);

  const doLogout = useCallback(async () => {
    setError(null);
    await logout();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      error,
      isAuthenticated: user !== null && !user.expired,
      login: doLogin,
      logout: doLogout,
    }),
    [user, loading, error, doLogin, doLogout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }
  return context;
}
